import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Cloud, Folder, HardDrive, ShieldCheck, CheckCircle, Warning,
  TestTube, FloppyDisk,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";

const BACKENDS = [
  { id: "emergent", name: "Emergent Object Storage", icon: Cloud,
    desc: "Managed by Emergent. Zero config. Best when you stay on the Emergent platform." },
  { id: "local",    name: "Local filesystem",        icon: HardDrive,
    desc: "Plain files on the server's disk. Best for a single-server self-host. Easy to back up with rsync." },
  { id: "azure",    name: "Azure Blob Storage",      icon: Folder,
    desc: "Pluggable cloud storage. Best when you self-host but want managed object storage." },
];

export default function StorageAdminPage() {
  const [loading, setLoading] = useState(true);
  const [state, setState] = useState(null);
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/storage");
      setState(data);
      setDraft({}); // reset on reload
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading || !state) {
    return <AppShell><div className="text-xs font-mono text-[--muted]" data-testid="storage-loading">Loading…</div></AppShell>;
  }

  const backend = draft.backend ?? state.saved.backend;
  const localDir = draft.local_dir ?? state.saved.local_dir;
  const azureContainer = draft.azure_container ?? state.saved.azure_container;
  const azureConnInput = draft.azure_connection_string;
  const hasAzureSaved = !!state.saved.azure_connection_string_preview;

  const isDirty = Object.keys(draft).length > 0;

  const onSave = async () => {
    setSaving(true);
    try {
      const body = {};
      if (draft.backend !== undefined) body.backend = draft.backend;
      if (draft.local_dir !== undefined) body.local_dir = draft.local_dir;
      if (draft.azure_container !== undefined) body.azure_container = draft.azure_container;
      if (draft.azure_connection_string !== undefined) body.azure_connection_string = draft.azure_connection_string;
      const { data } = await api.patch("/admin/storage", body);
      setState(data);
      setDraft({});
      toast.success(`Storage backend → ${data.backend}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/admin/storage/test");
      if (data.ok) toast.success(`Round-trip OK on ${data.backend}`);
      else toast.error(`Test failed: ${data.error || "unknown"}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setTesting(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <Link to="/admin" className="text-xs font-mono text-[--muted] hover:text-[--ink] inline-flex items-center gap-1">
          <ArrowLeft weight="bold" size={12} /> Admin
        </Link>
        <div className="mt-2 overline text-[--muted] inline-flex items-center gap-2">
          <ShieldCheck weight="bold" size={14} /> STORAGE
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Audio storage</h1>
        <p className="text-[--muted] mt-3 max-w-2xl text-sm">
          Pick where uploaded audio files live. Switching backends affects new uploads only —
          existing files keep their original location.
        </p>
      </div>

      {/* Backend picker */}
      <section className="mb-8" data-testid="backend-picker">
        <div className="overline text-[--muted] mb-3">BACKEND</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {BACKENDS.map((b) => {
            const Icon = b.icon;
            const selected = backend === b.id;
            const active = state.backend === b.id;
            return (
              <button
                key={b.id}
                type="button"
                onClick={() => setDraft((d) => ({ ...d, backend: b.id }))}
                data-testid={`backend-${b.id}`}
                className={`hard-border bg-white p-5 text-left transition-colors ${selected ? "ring-4 ring-[--accent] ring-offset-2" : "hover:bg-[--paper]"}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <Icon weight="duotone" size={32} />
                  {active && (
                    <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 bg-emerald-50 border border-emerald-500 text-emerald-700 inline-flex items-center gap-1">
                      <CheckCircle weight="bold" size={10} /> ACTIVE
                    </span>
                  )}
                </div>
                <div className="font-display font-black text-xl mt-3 tracking-tighter">{b.name}</div>
                <div className="text-xs text-[--muted] mt-2 leading-relaxed">{b.desc}</div>
              </button>
            );
          })}
        </div>
      </section>

      {/* Backend-specific config */}
      {backend === "local" && (
        <section className="mb-8 hard-border bg-white p-6" data-testid="local-config">
          <div className="overline text-[--muted] mb-2">LOCAL FILESYSTEM</div>
          <label className="text-xs font-bold uppercase tracking-widest text-[--muted]">Storage directory</label>
          <input
            type="text"
            value={localDir || ""}
            onChange={(e) => setDraft((d) => ({ ...d, local_dir: e.target.value }))}
            placeholder="/app/data/audio"
            data-testid="local-dir-input"
            className="block w-full mt-2 px-3 py-3 text-sm font-mono border border-[--ink] focus:outline-none focus:ring-2 focus:ring-[--accent]"
          />
          <p className="text-[11px] text-[--muted] font-mono mt-2 leading-relaxed">
            Must be writable by the backend process. The folder is created automatically. Defaults to
            <code className="ml-1">/app/data/audio</code> if blank.
          </p>
        </section>
      )}

      {backend === "azure" && (
        <section className="mb-8 hard-border bg-white p-6" data-testid="azure-config">
          <div className="overline text-[--muted] mb-2">AZURE BLOB</div>

          <label className="text-xs font-bold uppercase tracking-widest text-[--muted]">Connection string</label>
          <input
            type="password"
            value={azureConnInput ?? ""}
            onChange={(e) => setDraft((d) => ({ ...d, azure_connection_string: e.target.value }))}
            placeholder={hasAzureSaved ? state.saved.azure_connection_string_preview : "DefaultEndpointsProtocol=https;AccountName=…;AccountKey=…;EndpointSuffix=core.windows.net"}
            data-testid="azure-conn-input"
            className="block w-full mt-2 px-3 py-3 text-sm font-mono border border-[--ink] focus:outline-none focus:ring-2 focus:ring-[--accent]"
            autoComplete="new-password"
            spellCheck={false}
          />
          <p className="text-[11px] text-[--muted] font-mono mt-2 leading-relaxed">
            Azure Portal → Storage account → <strong>Access keys</strong> → Connection string. Leave blank to keep the current value.
          </p>

          <label className="text-xs font-bold uppercase tracking-widest text-[--muted] mt-5 block">Container name</label>
          <input
            type="text"
            value={azureContainer || ""}
            onChange={(e) => setDraft((d) => ({ ...d, azure_container: e.target.value }))}
            placeholder="flowscribe-audio"
            data-testid="azure-container-input"
            className="block w-full mt-2 px-3 py-3 text-sm font-mono border border-[--ink] focus:outline-none focus:ring-2 focus:ring-[--accent]"
          />
          <p className="text-[11px] text-[--muted] font-mono mt-2 leading-relaxed">
            Auto-created if it doesn't exist (when the connection string has permission). Defaults to <code>flowscribe-audio</code>.
          </p>
        </section>
      )}

      {backend === "emergent" && (
        <section className="mb-8 hard-border bg-[--sky] p-6 text-xs text-[--ink]/75 leading-relaxed" data-testid="emergent-config">
          Emergent Object Storage requires no configuration — authentication is derived from your Emergent Universal Key.
        </section>
      )}

      {/* Actions */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={onSave}
          disabled={!isDirty || saving}
          data-testid="storage-save-btn"
          className="bg-[--accent] text-white px-5 py-3 text-sm font-bold inline-flex items-center gap-2 hover:bg-[--accent-hi] transition-colors disabled:opacity-40"
        >
          <FloppyDisk weight="bold" size={14} />
          {saving ? "Saving…" : "Save backend"}
        </button>
        <button
          onClick={onTest}
          disabled={testing}
          data-testid="storage-test-btn"
          className="border border-[--ink] px-5 py-3 text-sm font-bold inline-flex items-center gap-2 hover:bg-[--ink] hover:text-white transition-colors disabled:opacity-40"
        >
          <TestTube weight="bold" size={14} />
          {testing ? "Probing…" : "Test connection"}
        </button>
        {state.backend === "azure" && !state.azure_configured && (
          <div className="text-xs text-amber-700 font-mono inline-flex items-center gap-2 ml-2">
            <Warning weight="bold" size={14} /> Azure is selected but no connection string is saved.
          </div>
        )}
      </div>
    </AppShell>
  );
}
